import React, { useState } from "react";
import axios from "axios";
import { useLang } from "../i18n";
import { ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${BACKEND}/api/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        role: form.role || null,
        message: form.message,
        language: lang,
      });
      setStatus({ state: "success", msg: t.contact.success });
      setForm({ name: "", email: "", company: "", role: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", msg: t.contact.error });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 border-t border-gt-cream/8">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8">
            <div className="section-eyebrow mb-6">
              <span className="gt-eyebrow">{t.contact.eyebrow}</span>
            </div>
            <h2 className="gt-display text-gt-cream text-4xl md:text-6xl lg:text-7xl max-w-3xl">
              {t.contact.title_a} <span className="block text-gt-cream/55 gt-display-light">{t.contact.title_em}</span>
            </h2>
            <p className="mt-8 text-base md:text-lg text-gt-ink/75 max-w-2xl leading-relaxed">{t.contact.subtitle}</p>

            <form onSubmit={submit} className="mt-12 space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-gt-mute mb-2" htmlFor="name">{t.contact.labels.name}</label>
                  <input id="name" data-testid="contact-name" type="text" required value={form.name} onChange={set("name")} placeholder={t.contact.placeholders.name} className="gt-input w-full px-4 py-4 text-base" />
                </div>
                <div>
                  <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-gt-mute mb-2" htmlFor="email">{t.contact.labels.email}</label>
                  <input id="email" data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder={t.contact.placeholders.email} className="gt-input w-full px-4 py-4 text-base" />
                </div>
                <div>
                  <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-gt-mute mb-2" htmlFor="company">{t.contact.labels.company}</label>
                  <input id="company" data-testid="contact-company" type="text" value={form.company} onChange={set("company")} placeholder={t.contact.placeholders.company} className="gt-input w-full px-4 py-4 text-base" />
                </div>
                <div>
                  <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-gt-mute mb-2" htmlFor="role">{t.contact.labels.role}</label>
                  <input id="role" data-testid="contact-role" type="text" value={form.role} onChange={set("role")} placeholder={t.contact.placeholders.role} className="gt-input w-full px-4 py-4 text-base" />
                </div>
              </div>
              <div>
                <label className="block text-[0.65rem] uppercase tracking-[0.22em] text-gt-mute mb-2" htmlFor="message">{t.contact.labels.message}</label>
                <textarea id="message" data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} placeholder={t.contact.placeholders.message} className="gt-input w-full px-4 py-4 text-base resize-y" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status.state === "loading"}
                  className="group inline-flex items-center justify-between gap-3 px-7 py-4 text-[0.7rem] btn-cream min-w-[200px] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "loading" ? t.contact.submitting : t.contact.submit}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
                {status.state === "success" && (
                  <span data-testid="contact-success" className="text-sm text-gt-cream">{status.msg}</span>
                )}
                {status.state === "error" && (
                  <span data-testid="contact-error" className="text-sm text-red-300">{status.msg}</span>
                )}
              </div>
              <p className="text-sm text-gt-ink/55 pt-2">
                {t.contact.direct_email}{" "}
                <a href="mailto:guilherme.trellese@gtstrategy.com.br" data-testid="direct-email" className="text-gt-cream hover:underline underline-offset-4">
                  guilherme.trellese@gtstrategy.com.br
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
